import { CircularProgress, makeStyles } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { useState } from 'react';
import { Field, Form } from 'react-final-form';
import { EventFullDetails } from '../../api/events';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { useScrollOnValidation } from '../../hooks/useScrollOnValidation';
import { formatDateToRFC } from '../../utils/date';
import { lengthField } from '../../utils/form';
import { validateEmail } from '../../utils/functions';
import { CLASS_TRACKING } from '../../utils/tracking_class';
import { length } from '../../utils/wysiwyg';
import {
  AttachFilesButton,
  BeforeUnload,
  Button,
  FormButtons,
  FormGroup,
  StickyContent,
} from '../common';
import AttachmentCard, {
  Attachment,
  getAttachmentFromFile,
} from '../common/attachment-card';
import { SelectOptions } from '../common/custom-select';
import {
  FormDateTimeInput,
  FormRating,
  FormSelect,
  TextFieldSummary,
  TextFieldWrapper,
  TextFieldWysiwyg,
} from '../forms/wrappers';

const useStyles = makeStyles((theme) => ({
  formBlocks: {
    display: 'flex',
    alignItems: 'flex-start',
  },

  formContainer: {
    width: '100%',
  },
  largeTextFormGroup: {
    maxWidth: '100%',
  },
  ventureUpdate: {
    '& .MuiInputBase-inputMultiline': {
      minHeight: 64,
    },
  },
  attachBtn: {
    marginTop: 24,
  },
  attachmentsList: {
    display: 'flex',
    flexWrap: 'wrap',
    margin: '20px 0 -16px -16px',
  },
  attachment: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0 0 16px 16px',

    [theme.breakpoints.up('xs')]: {
      width: '50%',
    },
  },
  progressVenture: {
    marginBottom: '10px !important',
  },
  formButtons: {
    justifyContent: 'flex-start',
  },
  fileName: {
    maxWidth: '200px !important',
  },
  actionsBlock: {
    marginTop: 56,
  },
}));

export interface FormValues {
  submitterEmail: string;
  summary: string;
  start: string;
  end: string;
  ventureProgressRating: number;
  achieveMentoringSession: string;
  happenedLastSession: string;
  goingHappen: string;
  additionalComments: string;
  attachments: string;
}

export interface FormInitialValues {
  submitterEmail: string;
  summary: string;
  start: string;
  end: string;
  ventureProgressRating: number;
  achieveMentoringSession: string;
  happenedLastSession: string;
  goingHappen: string;
  additionalComments: string;
  attachments: Attachment[];
}

interface Props {
  fullDetails: EventFullDetails;
  onSubmit: (values: FormValues) => void;
  onUploadFile: (file: File) => Promise<string | undefined>;
  loading: boolean;
  initialValues?: FormInitialValues;
  errorVaildDate?: boolean;
  isStickySubmit?: boolean;
  emailOptions?: SelectOptions[];
  hasBeforeUnload?: boolean;
}

type Errors = {
  [K in keyof FormValues]?: string;
};

const validateForm = (values: FormValues) => {
  const errors: Errors = {};

  if (!values.submitterEmail) {
    errors.submitterEmail = 'Required';
  } else if (!validateEmail(values.submitterEmail)) {
    errors.submitterEmail = 'Invalid';
  }
  if (!values.ventureProgressRating) {
    errors.ventureProgressRating = 'Required';
  }
  if (!values.achieveMentoringSession) {
    errors.achieveMentoringSession = 'Required';
  } else if (values.achieveMentoringSession.length > lengthField.agendaMaxLength) {
    errors.achieveMentoringSession = `Max ${lengthField.agendaMaxLength} characters (HTML)`;
  } else if (length(values.achieveMentoringSession) > lengthField.agendaDisplayLimit) {
    errors.achieveMentoringSession = `Max ${lengthField.agendaDisplayLimit} characters`;
  }
  if (!values.happenedLastSession) {
    errors.happenedLastSession = 'Required';
  } else if (values.happenedLastSession.length > lengthField.agendaMaxLength) {
    errors.happenedLastSession = `Max ${lengthField.agendaMaxLength} characters (HTML)`;
  } else if (length(values.happenedLastSession) > lengthField.agendaDisplayLimit) {
    errors.happenedLastSession = `Max ${lengthField.agendaDisplayLimit} characters`;
  }
  if (!values.goingHappen) {
    errors.goingHappen = 'Required';
  } else if (values.goingHappen.length > lengthField.agendaMaxLength) {
    errors.goingHappen = `Max ${lengthField.agendaMaxLength} characters (HTML)`;
  } else if (length(values.goingHappen) > lengthField.agendaDisplayLimit) {
    errors.goingHappen = `Max ${lengthField.agendaDisplayLimit} characters`;
  }

  if (values.additionalComments) {
    if (values.additionalComments.length > lengthField.agendaMaxLength) {
      errors.additionalComments = `Max ${lengthField.agendaMaxLength} characters (HTML)`;
    } else if (length(values.additionalComments) > lengthField.agendaDisplayLimit) {
      errors.additionalComments = `Max ${lengthField.agendaDisplayLimit} characters`;
    }
  }

  return errors;
};

const formatAttachments = (attachments: Attachment[]) => {
  try {
    if (!attachments.length) {
      return '';
    }
    const attachmentRefs = JSON.stringify(attachments);
    return attachmentRefs;
  } catch (e: any) {
    return '';
  }
};

function ReportAgendaRequestForm({
  onSubmit,
  loading,
  initialValues,
  onUploadFile,
  isStickySubmit,
  emailOptions,
  hasBeforeUnload,
}: Props) {
  const classes = useStyles();
  const isSubmitDisabled = loading;
  const { rb } = useResourceBundles();
  const setSubmitValidationFailed = useScrollOnValidation();

  const [attachments, setAttachments] = useState<Attachment[]>(
    initialValues?.attachments || [],
  );

  const handleSubmit = (values: FormValues) => {
    const attachmentRefs = formatAttachments(attachments);
    onSubmit({ ...values, attachments: attachmentRefs });
  };

  const handleSelectFile = (
    e: React.ChangeEvent<HTMLInputElement>,
    file: File,
  ) => {
    handleFileUpload(file);
    e.target.value = '';
  };

  const handleFileUpload = async (file: File) => {
    try {
      const fileIndex = attachments.length;
      setAttachments((prevAttachments) => [
        ...prevAttachments,
        getAttachmentFromFile(file),
      ]);
      const fileURL = await onUploadFile(file);
      setAttachments((prevAttachments) => {
        return prevAttachments.map((prevAttach, prevAttachIndex) => {
          if (prevAttachIndex !== fileIndex) {
            return prevAttach;
          }
          return {
            ...prevAttach,
            url: fileURL,
          };
        });
      });
    } catch (e: any) { }
  };

  const handleFileRemove = (index: number) => {
    setAttachments((prevAttachments) =>
      prevAttachments.filter((_, attachIndex) => attachIndex !== index),
    );
  };

  return (
    <Form
      validate={validateForm}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      keepDirtyOnReinitialize
      render={(formProps) => {
        setSubmitValidationFailed(
          formProps.submitFailed &&
          !formProps.dirtySinceLastSubmit &&
          !formProps.submitting,
        );
        return (
          <div className={classes.formBlocks}>
            <form noValidate className={classes.formContainer}>
              {hasBeforeUnload && (
                <BeforeUnload
                  when={formProps.dirty && !loading}
                  title='Leave the page'
                  body='You are about to leave the page, all unsaved changes will be lost. Do you want to continue?'
                  disabled={loading}
                  confirmButtonRenderer={({ onConfirm }) => (
                    <Button
                      variant='outlined'
                      onClick={async () => {
                        try {
                          await formProps.handleSubmit();
                          onConfirm();
                        } catch (e: any) { }
                      }}
                      disabled={loading || !formProps.valid}>
                      {loading ? (
                        <CircularProgress size={24} color='inherit' />
                      ) : (
                        'Save the changes'
                      )}
                    </Button>
                  )}
                />
              )}
              <FormGroup>
                <Field<string>
                  name='summary'
                  component={TextFieldSummary}
                  label='Summary'
                  disabled
                  testid='report-agenda-form-summary'
                />
              </FormGroup>
              <FormGroup mobile>
                <Field<string>
                  name='start'
                  component={FormDateTimeInput}
                  label='Start'
                  parse={(value) => formatDateToRFC(value)}
                  disabled
                  testId='report-agenda-form-start'
                />
                <Field<string>
                  name='end'
                  component={FormDateTimeInput}
                  label='End'
                  parse={(value) => formatDateToRFC(value)}
                  disabled
                  testId='report-agenda-form-end'
                />
              </FormGroup>
              {emailOptions ? (
                <FormGroup>
                  <Field<string>
                    name='submitterEmail'
                    testid='report-agenda-form-email'
                    component={FormSelect}
                    label='Author*'
                    options={emailOptions}
                  />
                </FormGroup>
              ) : (
                <FormGroup>
                  <Field<string>
                    name='submitterEmail'
                    disabled={!!initialValues?.submitterEmail}
                    component={TextFieldWrapper}
                    label='Email*'
                    testid='report-agenda-form-email'
                    formatOnBlur
                    format={(value: string) => {
                      return value ? value.toLowerCase() : value;
                    }}
                    InputProps={{
                      inputProps: {
                        maxLength: lengthField.email,
                      },
                    }}
                  />
                </FormGroup>
              )}
              <FormGroup>
                <Field<number>
                  name='ventureProgressRating'
                  component={FormRating}
                  label='How would you rate your progress as a founder in relation to your personal goals? *'
                  className={classes.progressVenture}
                  testid='report-agenda-form-venture-rating'
                />
              </FormGroup>
              <FormGroup className={classes.largeTextFormGroup}>
                <Field<string>
                  name='achieveMentoringSession'
                  label={`What do you want to achieve from this ${rb(
                    'mentoring',
                  )} session?*`}
                  placeholder='Type your answer here...'
                  component={TextFieldWysiwyg}
                  className={classes.ventureUpdate}
                  testid='report-agenda-form-achieve-mentoring-session'
                  showCount
                  maxLength={lengthField.agendaDisplayLimit}
                  InputProps={{
                    inputProps: {
                      maxLength: lengthField.ventureUpdate,
                    },
                  }}
                />
              </FormGroup>
              <FormGroup className={classes.largeTextFormGroup}>
                <Field<string>
                  name='happenedLastSession'
                  label='What 3 things happened since the last session?*'
                  placeholder='Type your answer here...'
                  component={TextFieldWysiwyg}
                  className={classes.ventureUpdate}
                  testid='report-agenda-form-happened-session'
                  showCount
                  maxLength={lengthField.agendaDisplayLimit}
                  InputProps={{
                    inputProps: {
                      maxLength: lengthField.ventureUpdate,
                    },
                  }}
                />
              </FormGroup>
              <FormGroup className={classes.largeTextFormGroup}>
                <Field<string>
                  name='goingHappen'
                  label='What 3 things are going to happen in the next 30 days?*'
                  placeholder='Type your answer here...'
                  component={TextFieldWysiwyg}
                  className={classes.ventureUpdate}
                  testid='report-agenda-form-going-happen'
                  showCount
                  maxLength={lengthField.agendaDisplayLimit}
                  InputProps={{
                    inputProps: {
                      maxLength: lengthField.ventureUpdate,
                    },
                  }}
                />
              </FormGroup>
              <FormGroup className={classes.largeTextFormGroup}>
                <Field<string>
                  name='additionalComments'
                  label='Any additional comments'
                  placeholder='Type here...'
                  component={TextFieldWysiwyg}
                  className={classes.ventureUpdate}
                  testid='report-agenda-form-additional-comments'
                  showCount
                  maxLength={lengthField.agendaDisplayLimit}
                  InputProps={{
                    inputProps: {
                      maxLength: lengthField.ventureUpdate,
                    },
                  }}
                />
              </FormGroup>
              <div className={classes.attachBtn}>
                <AttachFilesButton onChange={handleSelectFile} />
              </div>
              <div className={classes.attachmentsList}>
                {attachments.map((attachment, attachIndex) => (
                  <div key={attachIndex} className={classes.attachment}>
                    <AttachmentCard
                      attachment={attachment}
                      loading={!attachment.url}
                      onRemove={() => handleFileRemove(attachIndex)}
                      classesFileName={classes.fileName}
                    />
                  </div>
                ))}
              </div>
              {isStickySubmit ? (
                <div className={classes.actionsBlock}>
                  <StickyContent>
                    <Button
                      className={CLASS_TRACKING.INTERNAL_ACTION}
                      data-testid='button-submit-form'
                      onClick={formProps.handleSubmit}
                      disabled={isSubmitDisabled}
                      startIcon={<CheckIcon />}>
                      {loading ? (
                        <CircularProgress size={24} color='inherit' />
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </StickyContent>
                </div>
              ) : (
                <FormButtons className={classes.formButtons}>
                  <Button
                    data-testid='button-submit-form'
                    onClick={formProps.handleSubmit}
                    disabled={isSubmitDisabled}>
                    {loading ? (
                      <CircularProgress size={24} color='inherit' />
                    ) : (
                      'Save'
                    )}
                  </Button>
                </FormButtons>
              )}
            </form>
          </div>
        );
      }}
    />
  );
}

export default ReportAgendaRequestForm;
